/**
 * MaterialThumbnail — single card in the Material Gallery grid.
 *
 * Renders a smooth-shaded preview sphere for the given SceneMaterial via
 * the shared offscreen rig in materialThumbnailRenderer.ts. The rendered
 * data URL is memoized on a structural hash of the properties object, so
 * editing a slider triggers one re-render and nothing else.
 *
 * Overlays (linked indicator, user count) are shown in the corners to
 * keep the preview itself uncluttered — same visual weight as
 * library/LibraryCard.tsx.
 */

import { Link2 } from "lucide-react";
import { type CSSProperties, useMemo } from "react";
import type { SceneMaterial } from "../../modeling/types.ts";
import {
  COLOR_ACCENT,
  COLOR_BORDER,
  COLOR_SURFACE,
  COLOR_TEXT_DIM,
  COLOR_TEXT_ON_ACCENT,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
  RADIUS_CONTROL,
  RADIUS_DOT,
} from "../tokens.ts";
import { renderMaterialThumbnail } from "./materialThumbnailRenderer.ts";

interface MaterialThumbnailProps {
  name: string;
  properties: SceneMaterial;
  selected: boolean;
  linked?: boolean;
  userCount?: number;
  onSelect: () => void;
}

const CARD_BASE: CSSProperties = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  padding: 6,
  borderRadius: RADIUS_CONTROL,
  border: `1px solid ${COLOR_BORDER}`,
  background: COLOR_SURFACE,
  cursor: "pointer",
  userSelect: "none",
  transition: "border-color 0.12s, background 0.12s",
};

const CARD_SELECTED: CSSProperties = {
  borderColor: COLOR_ACCENT,
  background: `${COLOR_ACCENT}12`,
};

const PREVIEW_BOX: CSSProperties = {
  position: "relative",
  aspectRatio: "1 / 1",
  borderRadius: RADIUS_DOT,
  overflow: "hidden",
  background: "#1a1a1c",
};

const OVERLAY_BADGE: CSSProperties = {
  position: "absolute",
  top: 4,
  padding: "1px 5px",
  borderRadius: RADIUS_DOT,
  fontSize: FONT_SIZE_TINY,
  lineHeight: 1.3,
  color: COLOR_TEXT_ON_ACCENT,
  background: `${COLOR_ACCENT}cc`,
};

export function MaterialThumbnail({
  name,
  properties,
  selected,
  linked,
  userCount = 0,
  onSelect,
}: MaterialThumbnailProps) {
  // The rig is a pure function of serializable properties, so a structural
  // hash is a valid memo key. The `properties` reference churns on every
  // store update but the hash is stable for identical content.
  const src = useMemo(
    () => renderMaterialThumbnail(properties),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [properties],
  );

  return (
    <div
      onClick={onSelect}
      style={selected ? { ...CARD_BASE, ...CARD_SELECTED } : CARD_BASE}
    >
      <div style={PREVIEW_BOX}>
        <img
          src={src}
          alt={name}
          draggable={false}
          style={{ width: "100%", height: "100%", display: "block" }}
        />
        {userCount > 1 && (
          <span style={{ ...OVERLAY_BADGE, left: 4 }}>{userCount}</span>
        )}
        {linked && (
          <span
            title="Saved in library"
            style={{ ...OVERLAY_BADGE, right: 4, ...BADGE_ICON_PAD }}
          >
            <Link2 size={10} />
          </span>
        )}
      </div>
      <div
        style={{
          marginTop: 6,
          fontSize: FONT_SIZE_SMALL,
          color: selected ? COLOR_ACCENT : COLOR_TEXT_DIM,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: "center",
        }}
        title={name}
      >
        {name}
      </div>
    </div>
  );
}

const BADGE_ICON_PAD: CSSProperties = {
  padding: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
